import { Component, EventEmitter, Inject, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AtlasChatService } from '@secca/core/services/atlas/atlas-chat.service';
import { DialogBoundryService } from '@secca/core/services/dialog-boundry.service';
import { formatAtlasMessageTimestamp } from '@secca/shared/helpers/atlas/atlas-date-format-helper';
import { escapeRegExp } from '@secca/shared/helpers/string-helper';
import { AtlasChatHistoryMessage, AtlasChatHistorySession } from '@secca/shared/models/atlas/atlas-chat-history.model';

export interface AtlasChatHistoryDialogData {
  sessions: AtlasChatHistorySession[];
  preferredUsername?: string;
  hasMoreSessions?: boolean;
  isLoadingMoreSessions?: boolean;
}

@Component({
  selector: 'app-atlas-chatbot-chat-history-dialog',
  templateUrl: './atlas-chatbot-chat-history-dialog.component.html',
  styleUrls: ['./atlas-chatbot-chat-history-dialog.component.scss'],
  standalone: false
})
export class AtlasChatbotChatHistoryDialogComponent {
  private readonly contextFieldLabelsByNormalizedKey: Record<string, string> = {
    customerprofile: 'Customer Profile',
    customer_profile: 'Customer Profile',
    endusernationality: 'End-user Nationality',
    end_user_nationality: 'End-user Nationality',
    productname: 'Product Name',
    product_name: 'Product Name',
    insuranceperiodfrom: 'Insurance Period From',
    insurance_period_from: 'Insurance Period From',
    insuranceperiodto: 'Insurance Period To',
    insurance_period_to: 'Insurance Period To',
    wintersportenabled: 'Winter Sport Enabled',
    winter_sport_enabled: 'Winter Sport Enabled',
    incidentlocation: 'Incident Location',
    incident_location: 'Incident Location',
    incidentcauselevel1: 'Incident Cause Level 1',
    incident_cause_level_1: 'Incident Cause Level 1',
    incidentcauselevel2: 'Incident Cause Level 2',
    incident_cause_level_2: 'Incident Cause Level 2',
    incidentcauselevel3: 'Incident Cause Level 3',
    incident_cause_level_3: 'Incident Cause Level 3',
    incidentdescription: 'Incident Description',
    incident_description: 'Incident Description',
    maindiagnosis: 'Main Diagnosis',
    main_diagnosis: 'Main Diagnosis',
    departuredate: 'Departure Date',
    departure_date: 'Departure Date',
    arrivaldate: 'Arrival Date',
    arrival_date: 'Arrival Date',
    traveldestination: 'Destinations',
    travel_destination: 'Destinations',
    plannedtraveldestinations: 'Destinations',
    planned_travel_destinations: 'Destinations',
    cotravellers: 'Co-travellers',
    co_travellers: 'Co-travellers',
    purposeoftravel: 'Purpose of Travel',
    purpose_of_travel: 'Purpose of Travel',
    reporter: 'Reporter',
    incidentdate: 'Incident Date',
    incident_date: 'Incident Date'
  };
  readonly scoreOptions = [
    { score: 1, label: 'Very bad' },
    { score: 2, label: 'Bad' },
    { score: 3, label: 'Mixed' },
    { score: 4, label: 'Good' },
    { score: 5, label: 'Very good' }
  ];
  selectedSessionId: string | null = null;
  searchTerm = '';
  isEditingDescription = false;
  descriptionDraft = '';
  pendingRemoveSession: AtlasChatHistorySession | null = null;
  private highlightedMessageCache = new Map<string, string>();
  private highlightedPlainTextCache = new Map<string, string>();
  @Output() continueSession = new EventEmitter<AtlasChatHistorySession>();
  @Output() loadMoreSessions = new EventEmitter<void>();
  @Output() sessionChanged = new EventEmitter<{ sessionId: string; changes: Partial<AtlasChatHistorySession> }>();
  @Output() sessionRemoved = new EventEmitter<string>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AtlasChatHistoryDialogData,
    private dialogRef: MatDialogRef<AtlasChatbotChatHistoryDialogComponent>,
    private atlasChatService: AtlasChatService,
    public dialogBoundryService: DialogBoundryService
  ) {}

  close(): void {
    this.dialogRef.close();
  }

  selectSession(index: number): void {
    const session = this.filteredSessions[index];
    this.selectedSessionId = session?.id || null;
    this.isEditingDescription = false;
    this.descriptionDraft = '';
  }

  continueSelectedSession(): void {
    const session = this.selectedSession;
    if (session) {
      this.continueSession.emit(session);
    }
  }

  loadMore(): void {
    if (this.data?.isLoadingMoreSessions || !this.data?.hasMoreSessions) {
      return;
    }

    this.loadMoreSessions.emit();
  }

  removeSession(session: AtlasChatHistorySession, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();

    if (!session?.id || session.isCurrent) {
      return;
    }

    this.pendingRemoveSession = session;
  }

  cancelRemoveSession(): void {
    this.pendingRemoveSession = null;
  }

  confirmRemoveSession(): void {
    const session = this.pendingRemoveSession;
    if (!session?.id || session.isCurrent) {
      this.pendingRemoveSession = null;
      return;
    }

    const existing = this.data.sessions || [];
    this.data.sessions = existing.filter(item => item.id !== session.id);
    this.sessionRemoved.emit(session.id);
    this.pendingRemoveSession = null;

    if (this.selectedSessionId === session.id) {
      this.selectedSessionId = this.filteredSessions[0]?.id || null;
      this.isEditingDescription = false;
      this.descriptionDraft = '';
    }
  }

  onSearchTermChange(value: string): void {
    this.searchTerm = value || '';
    this.clearHighlightCaches();
    const firstMatch = this.filteredSessions[0];
    this.selectedSessionId = firstMatch?.id || null;
    this.isEditingDescription = false;
    this.descriptionDraft = '';
  }

  beginEditSelectedSessionDescription(): void {
    if (!this.selectedSession) {
      return;
    }
    this.isEditingDescription = true;
    this.descriptionDraft = this.selectedSession.sessionDescription || '';
  }

  saveSelectedSessionDescription(): void {
    const session = this.selectedSession;
    if (!session) {
      return;
    }

    session.sessionDescription = (this.descriptionDraft || '').trim();
    this.isEditingDescription = false;
    this.persistSessionChange(session.id, { sessionDescription: session.sessionDescription || '' });
  }

  setSelectedSessionScore(score: number): void {
    const session = this.selectedSession;
    if (!session) {
      return;
    }

    const nextScore = Math.max(0, Math.min(5, score));
    session.overallScore = nextScore;
    this.persistSessionChange(session.id, { overallScore: nextScore });
  }

  getSessionScore(session: AtlasChatHistorySession | null): number {
    if (!session || session.overallScore == null) {
      return 0;
    }

    return Math.max(0, Math.min(5, session.overallScore));
  }

  getSessionScoreText(session: AtlasChatHistorySession | null): string {
    const score = this.getSessionScore(session);
    if (score === 0) {
      return '0 - Not set';
    }

    const scoreLabel = this.scoreOptions.find(option => option.score === score)?.label || '';
    return `${score} - ${scoreLabel}`;
  }

  get filteredSessions(): AtlasChatHistorySession[] {
    const sessions = this.orderedSessions;
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return sessions;
    }

    return sessions.filter(session => {
      const sessionIdText = (session.sessionId || '').toLowerCase();
      const caseNumberText = (session.caseNumber || '').toLowerCase();
      const matchesSessionId = sessionIdText.includes(term);
      const matchesCaseNumber = caseNumberText.includes(term);

      // Case-number-like terms should only match via the session case number (or Session ID itself).
      if (this.isLikelyCaseNumberTerm(term) && !matchesCaseNumber && !matchesSessionId) {
        return false;
      }

      const messagesText = (session.messages || [])
        .map(message => [
          message.text || '',
          this.stripHtml(message.html || ''),
          message.reactionComment || '',
          this.formatEnabledFieldLabels(message.caseContextEnabledFields).join(' ')
        ].join(' '))
        .join(' ');

      const haystack = [
        session.sessionId || '',
        session.sessionDescription || '',
        session.caseNumber || '',
        messagesText
      ].join(' ').toLowerCase();
      return haystack.includes(term);
    });
  }

  private get orderedSessions(): AtlasChatHistorySession[] {
    const sessions = this.data?.sessions || [];
    return sessions
      .slice()
      .sort((first, second) => this.getSessionSortTime(second) - this.getSessionSortTime(first));
  }

  get selectedSession(): AtlasChatHistorySession | null {
    const sessions = this.filteredSessions;
    if (sessions.length === 0) {
      return null;
    }

    if (this.selectedSessionId) {
      const selectedById = sessions.find(session => session.id === this.selectedSessionId);
      if (selectedById) {
        return selectedById;
      }
    }

    return sessions[0];
  }

  isSelectedSession(session: AtlasChatHistorySession, index: number): boolean {
    if (!session) {
      return false;
    }

    if (this.selectedSessionId) {
      return session.id === this.selectedSessionId;
    }

    return index === 0;
  }

  getCaseNumberText(session: AtlasChatHistorySession): string {
    return session?.caseNumber || '-';
  }

  getContinueButtonText(session: AtlasChatHistorySession): string {
    return session?.isCurrent ? 'Current conversation' : 'Continue conversation';
  }

  formatSessionListTimestamp(session: AtlasChatHistorySession): string {
    const latestMessageTimestamp = this.getLatestMessageTimestamp(session);
    return this.formatTimestamp(latestMessageTimestamp || session?.createdAt || '');
  }

  formatTimestamp(value: string): string {
    if (!value) {
      return '-';
    }

    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(parsed);
  }

  private getLatestMessageTimestamp(session: AtlasChatHistorySession): string | null {
    const latestTimestamp = (session?.messages || [])
      .map(message => message.timestamp)
      .filter((timestamp): timestamp is string => !!timestamp && !isNaN(new Date(timestamp).getTime()))
      .sort((first, second) => new Date(second).getTime() - new Date(first).getTime())[0];

    return latestTimestamp || null;
  }

  private getSessionSortTime(session: AtlasChatHistorySession): number {
    const latestMessageTimestamp = this.getLatestMessageTimestamp(session);
    const parsed = new Date(latestMessageTimestamp || session?.createdAt || '').getTime();
    return isNaN(parsed) ? 0 : parsed;
  }

  formatMessageTimestamp(value: string | null | undefined): string {
    return formatAtlasMessageTimestamp(value);
  }

  getFeedbackSummary(message: AtlasChatHistoryMessage): string {
    const reactionText = message?.reaction ? `Reaction: ${message.reaction}` : 'Reaction: none';
    const commentText = message?.reactionComment ? `Comment: ${message.reactionComment}` : 'Comment: none';
    return `${reactionText} • ${commentText}`;
  }

  getEnabledFieldsText(message: AtlasChatHistoryMessage): string {
    const enabledFields = this.formatEnabledFieldLabels(message?.caseContextEnabledFields);
    return `Enabled fields: ${enabledFields.length ? enabledFields.join(', ') : '-'}`;
  }

  getHighlightedMessageHtml(message: AtlasChatHistoryMessage): string {
    const cacheKey = this.createMessageHighlightCacheKey(message);
    const cached = this.highlightedMessageCache.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }

    const term = this.searchTerm.trim();
    let result: string;
    if (!term) {
      result = message?.html || this.escapeHtml(message?.text || '');
    } else if (message?.html) {
      result = this.highlightInHtml(message.html, term);
    } else {
      result = this.highlightInText(message?.text || '', term);
    }

    this.highlightedMessageCache.set(cacheKey, result);
    return result;
  }

  getHighlightedPlainText(value: string): string {
    const cacheKey = `${this.searchTerm.trim()}\u0000${value || ''}`;
    const cached = this.highlightedPlainTextCache.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }

    const term = this.searchTerm.trim();
    let result: string;
    if (!term) {
      result = this.escapeHtml(value || '');
    } else {
      result = this.highlightInText(value || '', term);
    }

    this.highlightedPlainTextCache.set(cacheKey, result);
    return result;
  }

  onMessageAreaKeydown(event: KeyboardEvent, container: HTMLElement): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      container.scrollBy({ top: 48, behavior: 'smooth' });
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      container.scrollBy({ top: -48, behavior: 'smooth' });
    }
  }

  private stripHtml(value: string): string {
    if (!value) {
      return '';
    }
    return value.replace(/<[^>]*>/g, ' ');
  }

  private formatEnabledFieldLabels(fields: string[] | null | undefined): string[] {
    return (fields || [])
      .map(field => this.formatEnabledFieldLabel(field))
      .filter(Boolean);
  }

  private formatEnabledFieldLabel(field: string | null | undefined): string {
    const rawField = (field || '').trim();
    if (!rawField) {
      return '';
    }

    const normalizedKey = rawField
      .replace(/[\s-]+/g, '_')
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .toLowerCase();
    const compactKey = normalizedKey.replace(/_/g, '');
    return this.contextFieldLabelsByNormalizedKey[normalizedKey] ||
      this.contextFieldLabelsByNormalizedKey[compactKey] ||
      this.toTitleCase(rawField);
  }

  private toTitleCase(value: string): string {
    return value
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .split(/\s+/)
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  private persistSessionChange(sessionId: string, changes: Partial<AtlasChatHistorySession>): void {
    if (!sessionId) {
      return;
    }

    const sessions = this.data.sessions || [];
    const target = sessions.find(session => session.id === sessionId);
    if (!target) {
      return;
    }

    Object.assign(target, changes);
    const request = {
      ...(changes.sessionDescription !== undefined ? { session_description: changes.sessionDescription } : {}),
      ...(changes.overallScore !== undefined ? { overall_score: changes.overallScore } : {})
    };

    this.atlasChatService.updateAtlasChatSession(sessionId, request).subscribe({
      next: () => this.sessionChanged.emit({ sessionId, changes }),
      error: () => {}
    });
  }

  private clearHighlightCaches(): void {
    this.highlightedMessageCache.clear();
    this.highlightedPlainTextCache.clear();
  }

  private createMessageHighlightCacheKey(message: AtlasChatHistoryMessage): string {
    return [
      this.searchTerm.trim(),
      message?.id || '',
      message?.html || '',
      message?.text || ''
    ].join('\u0000');
  }

  private highlightInHtml(html: string, term: string): string {
    const container = document.createElement('div');
    container.innerHTML = html;
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];
    while (walker.nextNode()) {
      textNodes.push(walker.currentNode as Text);
    }

    textNodes.forEach(node => {
      const value = node.nodeValue || '';
      const fragment = this.buildHighlightedFragment(value, term);
      if (fragment) {
        node.parentNode?.replaceChild(fragment, node);
      }
    });

    return container.innerHTML;
  }

  private highlightInText(value: string, term: string): string {
    const escaped = this.escapeHtml(value);
    const escapedTerm = escapeRegExp(this.escapeHtml(term));
    if (!escapedTerm) {
      return escaped;
    }
    const pattern = new RegExp(`(${escapedTerm})`, 'ig');
    return escaped.replace(pattern, '<mark class="history-highlight">$1</mark>');
  }

  private buildHighlightedFragment(value: string, term: string): DocumentFragment | null {
    if (!value) {
      return null;
    }

    const pattern = new RegExp(escapeRegExp(term), 'ig');
    const matches: Array<{ index: number; text: string }> = [];
    let matchResult: RegExpExecArray | null = pattern.exec(value);
    while (matchResult) {
      matches.push({
        index: matchResult.index,
        text: matchResult[0]
      });
      matchResult = pattern.exec(value);
    }

    if (matches.length === 0) {
      return null;
    }

    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    matches.forEach(match => {
      const index = match.index || 0;
      if (index > lastIndex) {
        fragment.appendChild(document.createTextNode(value.slice(lastIndex, index)));
      }

      const mark = document.createElement('mark');
      mark.className = 'history-highlight';
      mark.textContent = match.text;
      fragment.appendChild(mark);
      lastIndex = index + match.text.length;
    });

    if (lastIndex < value.length) {
      fragment.appendChild(document.createTextNode(value.slice(lastIndex)));
    }

    return fragment;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private isLikelyCaseNumberTerm(value: string): boolean {
    if (!value) {
      return false;
    }

    return /[a-z]{2,}[- ]?\d|^\d{4,}$/.test(value);
  }
}
